import { useQuery } from "@tanstack/react-query";
import { format, isSameDay, startOfDay } from "date-fns";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useMemo } from "react";

import { CommunitySupportWidget } from "@/components/Analytics/CommunitySupportWidget";
import { FahWidget } from "@/components/Analytics/FahWidget";
import { PlatformReachWidget } from "@/components/Analytics/PlatformReachWidget";
import { PlatformResidentsWidget } from "@/components/Analytics/PlatformResidentsWidget";
import { TicketResolutionTimeWidget } from "@/components/Analytics/TicketResolutionTimeWidget";
import { TicketSatisfactionWidget } from "@/components/Analytics/TicketSatisfactionWidget";
import type { Period } from "@/components/Analytics/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { Share01Icon } from "@/components/Icons/Share01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { PeriodPicker } from "@/components/PeriodPicker/PeriodPicker";
import { RadioCard } from "@/components/Radio/RadioCard";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Skeleton } from "@/design-system/Skeleton";
import { Stack } from "@/design-system/Stack";
import { MAX_ANALYTICS_DATE, MIN_ANALYTICS_DATE } from "@/helpers/analytics";
import { parseQueryParamToDate } from "@/helpers/query-params";
import { useProject } from "@/hooks/Network/useProject";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useQueryParams } from "@/hooks/useQueryParam";
import { usePortfolioAnalyticsQueries } from "@/queries/portfolio-analytics/queryOptions";
import { portfolioMutations } from "@/queries/portfolio/mutations";
import { useTranslation } from "@/translations";

import type { ProjectHomeRouteParams } from "../../routes";

export function ProjectHomePage(): React.ReactNode {
  const clockDate = useCurrentDate();
  const [queryParams, setQueryParams] = useQueryParams<ProjectHomeRouteParams["home"]>();
  const [isConfirmExportModalOpened, confirmExportModalHandler] = useBool();

  const sessionUser = useSessionUser();
  const { t, i18n } = useTranslation();
  const postHog = usePostHog();
  const showFlashToast = useFlashToast();
  const project = useProject();

  const portfolioAnalyticsQueries = usePortfolioAnalyticsQueries();
  const { data: fahAnalytics } = useQuery({
    ...portfolioAnalyticsQueries.fah({
      projectId: project.id,
    }),
    enabled: project.type !== "companyBased",
  });
  // Use community support analytics to render last updated date (as this is used in both address and company based projects)
  const { data: communitySupportAnalytics } = useQuery(
    portfolioAnalyticsQueries.communitySupport({
      projectId: project.id,
    }),
  );

  const exportProjectAnalyticsMutation = portfolioMutations.useExportProjectAnalytics();

  const currFilter = useMemo(() => {
    return {
      startDate: parseQueryParamToDate(queryParams.startDate),
    };
  }, [queryParams.startDate]);

  // Update date period filter
  const handleUpdateDatePeriod = (from: Date | undefined) => {
    setQueryParams({
      ...queryParams,
      startDate: from ? format(from, "yyyy-MM-dd") : undefined,
    });
  };

  const exportAnalytics = () => {
    exportProjectAnalyticsMutation.mutate(
      {
        data: {
          projectIds: [project.id],
        },
        fileName: `${project.name}_analytics_${format(new Date(), "yyyy-MM-dd")}_last_90_days.xlsx`,
      },
      {
        onSettled: () => {
          confirmExportModalHandler.setFalse();
        },
      },
    );
  };

  const handleSharePage = () => {
    const urlToBeShared = window.location.href;

    void navigator.clipboard
      .writeText(urlToBeShared)
      .then(() => {
        postHog.capture("clicked_share_project_page", {
          url: urlToBeShared,
        });
      })
      .then(() => {
        showFlashToast({
          variant: "success",
          message: t("page.portfolio-home.share.notifications.success"),
        });
      })
      .catch(() => {
        showFlashToast({
          variant: "alert",
          message: t("page.portfolio-home.share.notifications.failed"),
        });
      });
  };

  const handleExportAnalytics = () => {
    postHog.capture("clicked_export_project_analytics");

    confirmExportModalHandler.setTrue();
  };

  // Create period object from currFilter
  const period: Period = currFilter.startDate
    ? { period: "quarter", startDate: currFilter.startDate }
    : { period: "last30Days" };

  const dateNow = clockDate;
  let greeting = t("common.greeting.morning");
  if (dateNow.getHours() >= 12 && dateNow.getHours() < 18) {
    greeting = t("common.greeting.afternoon");
  } else if (dateNow.getHours() >= 18) {
    greeting = t("common.greeting.evening");
  }

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={sessionUser.project.name}
        title={`${greeting}, ${sessionUser.firstName}`}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: sessionUser.project.name,
              },
              {
                name: t("page.home.title"),
              },
            ]}
          />
        }
        actions={
          <>
            <AskNobiButton
              defaultFilters={{ projectIds: [project.id] }}
              context={{ scope: "project", entity: "home", page: "detail" }}
              suggestions={[
                t("component.ask-nobi-chat-drawer.home.suggestion.1"),
                t("component.ask-nobi-chat-drawer.home.suggestion.2"),
              ]}
            />
            <Button onClick={handleSharePage} icon={<Share01Icon />} variant="soft">
              {t("common.action.share")}
            </Button>
            <Button onClick={handleExportAnalytics} icon={<Download01Icon />}>
              {t("page.portfolio-home.btn.export")}
            </Button>
          </>
        }
        header={
          <Inline gap="md" align="center" justify="between">
            <PeriodPicker
              type="quarter"
              value={currFilter.startDate ? startOfDay(currFilter.startDate) : dateNow}
              onChange={(startDate) => handleUpdateDatePeriod(startDate)}
              min={MIN_ANALYTICS_DATE}
              max={MAX_ANALYTICS_DATE}
              renderSelected={(date) => {
                if (date.getTime() === dateNow.getTime()) {
                  return t("page.portfolio.overview.filters.month.latest");
                }

                return formatDate(i18n, "quarter", date);
              }}
              renderFooterComponent={
                fahAnalytics
                  ? (onClose) => (
                      <section className="py-space-sm">
                        <RadioCard
                          name="last30Days"
                          value={dateNow.toDateString()}
                          onChange={() => {
                            handleUpdateDatePeriod(undefined);
                            onClose();
                          }}
                          checked={currFilter.startDate ? isSameDay(currFilter.startDate, dateNow) : true}
                          label={t("page.portfolio.overview.filters.month.latest")}
                          description={`${formatDate(i18n, "dateMonthYearShort", fahAnalytics.startDate)} - ${formatDate(i18n, "dateMonthYearShort", fahAnalytics.endDate)}`}
                        />
                      </section>
                    )
                  : undefined
              }
            />
            {communitySupportAnalytics?.endDate ? (
              <p className="text-caption text-text-secondary">
                {t("page.portfolio-home.label.last-updated-at", {
                  datetime: formatDate(i18n, "datetime", communitySupportAnalytics.endDate),
                })}
              </p>
            ) : (
              <Skeleton width="40" height="4" />
            )}
          </Inline>
        }
      >
        <Stack gap="lg">
          {project.type !== "companyBased" && (
            <PageGrid.Row>
              <PageGrid.Column size="100%">
                <FahWidget projectIds={[project.id]} period={period} />
              </PageGrid.Column>
            </PageGrid.Row>
          )}

          <PageGrid.Row>
            <PageGrid.Column size="100%">
              <h2 className="text-heading-2 text-text-brand-secondary">
                {t("page.portfolio-home.community-resilience.title")}
              </h2>
              <PageGrid.Row>
                <PageGrid.Column size="50%">
                  <CommunitySupportWidget projectIds={[project.id]} period={period} />
                </PageGrid.Column>

                <PageGrid.Column size="50%">
                  <PlatformResidentsWidget projectIds={[project.id]} type="supportive" period={period} />
                </PageGrid.Column>
              </PageGrid.Row>
            </PageGrid.Column>
          </PageGrid.Row>

          <PageGrid.Row>
            <PageGrid.Column size="100%">
              <h2 className="text-heading-2 text-text-brand-secondary">{t("page.portfolio-home.tickets.title")}</h2>
              <PageGrid.Row>
                <PageGrid.Column size="50%">
                  <TicketSatisfactionWidget projectIds={[project.id]} period={period} />
                </PageGrid.Column>

                <PageGrid.Column size="50%">
                  <TicketResolutionTimeWidget projectIds={[project.id]} period={period} />
                </PageGrid.Column>
              </PageGrid.Row>
            </PageGrid.Column>
          </PageGrid.Row>

          <PageGrid.Row>
            <PageGrid.Column size="100%">
              <h2 className="text-heading-2 text-text-brand-secondary">
                {t("page.portfolio-home.community-engagement.title")}
              </h2>
              <PageGrid.Row>
                <PageGrid.Column size="50%">
                  <PlatformReachWidget projectIds={[project.id]} period={period} />
                </PageGrid.Column>

                <PageGrid.Column size="50%">
                  <PlatformResidentsWidget projectIds={[project.id]} type="onboarded" period={period} />
                  <PlatformResidentsWidget projectIds={[project.id]} type="engaging" period={period} />
                  <PlatformResidentsWidget projectIds={[project.id]} type="contributing" period={period} />
                </PageGrid.Column>
              </PageGrid.Row>
            </PageGrid.Column>
          </PageGrid.Row>
        </Stack>
      </DocumentPage>

      <ConfirmModal
        tone="primary"
        isOpened={isConfirmExportModalOpened}
        onOpenChange={confirmExportModalHandler.set}
        isLoading={exportProjectAnalyticsMutation.isPending}
        title={t("page.home.export-analytics-modal.title")}
        description={t("page.home.export-analytics-modal.description")}
        data-testid="project-home-confirm-export-modal"
        onReject={() => confirmExportModalHandler.setFalse()}
        onResolve={exportAnalytics}
        resolveBtnProps={{
          text: t("common.action.export"),
          "data-testid": "confirm-export-btn",
        }}
      />
    </>
  );
}
